<template>
    <div id="content" class="andy-agents" :class="[$mq, { 'is-chat': hasConversation }]">
        <div class="content full hide-scrollbar">
            <div v-if="!hasConversation" class="andy-agents__intro">
                <AndyMascot decorative :size="40" />
                <p class="andy-agents__title">{{ $t('login.wizard.title') }}</p>
            </div>

            <div v-else ref="thread" class="andy-agents__thread hide-scrollbar">
                <article v-for="message in messages" :key="message.id" :data-message-id="message.id" class="andy-agents__message" :class="['is-' + message.role, { 'has-widgets': hasWidgets(message) }]">
                    <div v-if="hasText(message)" class="andy-agents__copy">
                        <p v-for="(paragraph, index) in messageParagraphs(message)" :key="index">{{ paragraph }}</p>
                    </div>
                    <Widget v-for="widget in messageWidgets(message)" :key="widget.id" :title="widget.title" :icon="widget.icon" :description="widget.description" :tool="widget.tool || ''" :route="widget.route || widget.moreRoute || ''" :visible-rows="widget.visibleRows" :total="widget.total" :offset="widget.offset || 0" :next-cursor="widget.nextCursor || ''" :example-id="widget.exampleId || ''" :rows="widget.rows" :empty-text="widget.emptyText || ''" :buttons="widget.buttons" @show-next="onWidgetShowNext" />
                    <FeedbackMenu v-if="canRate(message)" @rate="rateMessage(message, $event)" />
                </article>

                <div v-if="isFetchingReply" ref="thinking" class="andy-agents__thinking" role="status">
                    <AndyMascot class="andy-agents__thinking-mascot" state="thinking" :size="24" decorative />
                    <p class="andy-agents__thinking-text">{{ $t('andy_agents.thinking') }}</p>
                </div>
            </div>

            <form class="andy-agents__dock" @submit.prevent="sendPrompt">
                <div class="andy-agents__composer">
                    <textarea ref="prompt" v-model="prompt" class="andy-agents__input" rows="1" :aria-label="$t('page_title.AndyAgents')" @input="resizePrompt" @keydown.enter="onPromptKeydown"></textarea>
                    <button type="submit" class="andy-agents__send" :disabled="isFetchingReply" :aria-label="$t('nps.send')">
                        <i class="i-andy-send i-andy-sm" aria-hidden="true"></i>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import AndyMascot from '@/components/domain/ui/AndyMascot.vue'
import FeedbackMenu from '@/components/domain/andy-agents/FeedbackMenu.vue'
import Widget from '@/components/domain/andy-agents/Widget.vue'

const PROMPT_MIN_HEIGHT = 52
const PROMPT_WELCOME_MIN_HEIGHT = 88
const PROMPT_MAX_HEIGHT = 227

export default {
    name: 'AndyAgents',
    components: { AndyMascot, FeedbackMenu, Widget },
    data() {
        return {
            prompt: '',
            messages: [],
            nextMessageId: 1,
            isFetchingReply: false
        }
    },
    computed: {
        hasConversation() {
            return this.messages.length > 0
        },
        hasLoginUser() {
            return this.$store.getters['loginUser/getHasLoginUser']
        }
    },
    watch: {
        hasLoginUser: {
            immediate: true,
            handler(isLogged) {
                if (!isLogged) {
                    this.leaveWhenLoggedOut()
                }
            }
        },
        hasConversation(isChat) {
            if (isChat) {
                this.$nextTick(this.resizePrompt)
                return
            }

            const input = this.$refs.prompt
            if (input) {
                input.style.height = ''
            }
        }
    },
    methods: {
        leaveWhenLoggedOut() {
            if (this.$store.getters['loginUser/getHasLoginUser']) {
                return
            }

            const target = this.$store.getters.getRouteAfterEmployeeLogout || '/home'
            if (this.$route.path === target) {
                return
            }

            this.$router.replace(target).catch((err) => {
                if (!err || err.name === 'NavigationDuplicated') {
                    return
                }
                logError(err)
            })
        },
        hasText(message) {
            return Boolean(message && message.text)
        },
        hasWidgets(message) {
            return this.messageWidgets(message).length > 0
        },
        canRate(message) {
            return Boolean(message && message.role === 'assistant' && message.canRate === true)
        },
        messageWidgets(message) {
            if (!message || !Array.isArray(message.widgets)) {
                return []
            }
            return message.widgets
        },
        messageParagraphs(message) {
            if (!this.hasText(message)) {
                return []
            }
            return message.text.split('\n\n')
        },
        onPromptKeydown(event) {
            if (event.isComposing) {
                return
            }

            if (event.altKey) {
                event.preventDefault()
                this.insertPromptLineBreak()
                return
            }

            if (event.shiftKey || event.metaKey || event.ctrlKey) {
                return
            }

            event.preventDefault()
            this.sendPrompt()
        },
        insertPromptLineBreak() {
            const input = this.$refs.prompt
            if (!input) {
                this.prompt += '\n'
                this.$nextTick(this.resizePrompt)
                return
            }

            const start = input.selectionStart
            const end = input.selectionEnd
            this.prompt = this.prompt.slice(0, start) + '\n' + this.prompt.slice(end)
            this.$nextTick(() => {
                const cursor = start + 1
                input.selectionStart = cursor
                input.selectionEnd = cursor
                this.resizePrompt()
            })
        },
        async sendPrompt() {
            const text = this.prompt.trim()
            if (!text || this.isFetchingReply) {
                return
            }

            const userMessageId = this.nextMessageId++
            this.messages.push({
                id: userMessageId,
                role: 'user',
                text: text
            })
            this.prompt = ''
            this.isFetchingReply = true
            let assistantMessageId = null

            await this.$nextTick()
            this.resizePrompt()
            this.scrollLoadingIntoView(userMessageId)

            try {
                const reply = await this.$store.dispatch('andyAgents/fetchReply', text)
                assistantMessageId = this.pushAssistantReply(reply)
            } catch (err) {
                logError(err)
            } finally {
                this.isFetchingReply = false
            }

            this.$nextTick(() => {
                this.resizePrompt()

                this.scrollSentExchangeIntoView(userMessageId, assistantMessageId)
            })
        },
        async onWidgetShowNext(payload, done) {
            if (this.isFetchingReply) {
                this.finishShowNext(done, false)
                return
            }

            this.isFetchingReply = true
            await this.$nextTick()
            this.scrollLoadingIntoView()

            let accepted = false
            let messageId = null
            try {
                const reply = await this.$store.dispatch('andyAgents/fetchWidgetNext', payload)
                messageId = this.pushAssistantReply(reply)
                accepted = true
            } catch (err) {
                logError(err)
            } finally {
                this.isFetchingReply = false
                this.finishShowNext(done, accepted)
            }

            if (!messageId) {
                return
            }
            this.$nextTick(() => {
                this.scrollFirstWidgetRowIntoView(messageId)
            })
        },
        finishShowNext(done, accepted) {
            if (typeof done !== 'function') {
                return
            }
            done(accepted)
        },
        pushAssistantReply(reply) {
            const widgets = reply && Array.isArray(reply.widgets) ? reply.widgets : []
            const id = this.nextMessageId++
            this.messages.push({
                id: id,
                role: 'assistant',
                canRate: Boolean(reply && reply.canRate === true),
                text: reply && reply.text ? reply.text : '',
                widgets: widgets
            })
            return id
        },
        rateMessage(message, feedback) {
            if (!message || !message.id) {
                return
            }

            this.$store
                .dispatch('andyAgents/submitFeedback', {
                    messageId: message.id,
                    feedback: feedback
                })
                .catch((err) => {
                    logError(err)
                })
        },
        resizePrompt() {
            if (!this.hasConversation) {
                return
            }

            const input = this.$refs.prompt
            if (!input) {
                return
            }

            input.style.height = 'auto'
            input.style.maxHeight = 'none'
            const nextHeight = Math.min(Math.max(input.scrollHeight, PROMPT_MIN_HEIGHT), PROMPT_MAX_HEIGHT)
            input.style.maxHeight = ''
            input.style.height = nextHeight + 'px'
        },
        scrollLoadingIntoView(userMessageId) {
            const thread = this.$refs.thread
            const loading = this.$refs.thinking
            if (!thread || !loading) {
                return
            }

            const loadingTop = this.topInContainer(thread, loading)
            const loadingBottom = loadingTop + loading.offsetHeight
            const userArticle = userMessageId ? this.messageArticle(thread, userMessageId) : null
            const rangeTop = userArticle ? this.topInContainer(thread, userArticle) : loadingTop
            this.scrollRangeIntoView(thread, rangeTop, loadingBottom)
        },
        scrollSentExchangeIntoView(userMessageId, assistantMessageId) {
            const thread = this.$refs.thread
            const userArticle = thread && this.messageArticle(thread, userMessageId)
            if (!userArticle) {
                return
            }

            const userTop = this.topInContainer(thread, userArticle)
            const replyStart = this.replyStartElement(thread, assistantMessageId)
            if (!replyStart) {
                this.scrollRangeIntoView(thread, userTop, userTop + userArticle.offsetHeight)
                return
            }

            const replyTop = this.topInContainer(thread, replyStart)
            const gap = Math.max(replyTop - (userTop + userArticle.offsetHeight), 0)
            const available = thread.clientHeight - userArticle.offsetHeight - gap
            let peek = Math.min(replyStart.offsetHeight, Math.max(available, 0))
            if (peek <= 0) {
                peek = Math.min(replyStart.offsetHeight, Math.round(thread.clientHeight / 3))
            }
            this.scrollRangeIntoView(thread, userTop, replyTop + peek)
        },
        scrollRangeIntoView(thread, targetTop, targetBottom) {
            const minScroll = Math.max(targetBottom - thread.clientHeight, 0)
            if (targetBottom - targetTop > thread.clientHeight) {
                thread.scrollTop = minScroll
                return
            }
            if (thread.scrollTop < minScroll) {
                thread.scrollTop = minScroll
                return
            }
            if (thread.scrollTop > targetTop) {
                thread.scrollTop = Math.max(targetTop, 0)
            }
        },
        replyStartElement(thread, messageId) {
            const article = this.messageArticle(thread, messageId)
            if (!article) {
                return null
            }
            return article.querySelector('.widget__row') || article.querySelector('.andy-agents__copy') || article
        },
        messageArticle(thread, messageId) {
            if (messageId === null || messageId === undefined || messageId === '') {
                return null
            }
            return thread.querySelector('[data-message-id="' + messageId + '"]')
        },
        scrollFirstWidgetRowIntoView(messageId) {
            const thread = this.$refs.thread
            if (!thread) {
                return
            }
            const article = this.messageArticle(thread, messageId)
            const firstRow = article && article.querySelector('.widget__row')
            if (!firstRow) {
                return
            }

            const rowTop = this.topInContainer(thread, firstRow)
            const rowBottom = rowTop + firstRow.offsetHeight
            const viewBottom = thread.scrollTop + thread.clientHeight
            if (rowTop >= thread.scrollTop && rowBottom <= viewBottom) {
                return
            }

            if (firstRow.offsetHeight >= thread.clientHeight) {
                thread.scrollTop = rowTop
                return
            }
            thread.scrollTop = Math.max(rowBottom - thread.clientHeight, 0)
        },
        topInContainer(container, element) {
            const containerRect = container.getBoundingClientRect()
            const elementRect = element.getBoundingClientRect()
            return container.scrollTop + (elementRect.top - containerRect.top)
        }
    }
}
</script>

<style lang="scss" scoped>
.andy-agents {
    > .content {
        @include display-flex();
        @include flex-direction(column);
        @include align-items(center);
        @include justify-content(center);
        height: 100%;
        padding: $alex-app-padding;
        gap: rem(16px);
    }

    &.is-chat > .content {
        @include justify-content(flex-start);
        min-height: 0;
    }

    &.portrait {
        .andy-agents__title,
        .andy-agents__input,
        .andy-agents__message,
        .andy-agents__copy {
            @include font-size(s);
        }
    }
}

.andy-agents__intro {
    @include display-flex();
    @include align-items(center);
    width: 100%;
    max-width: rem(720px);
    gap: rem(12px);
}

.andy-agents__title {
    @include font-size(ml);
    margin: 0;
    font-family: $text;
    color: $color-black;
}

.andy-agents__thread {
    @include display-flex();
    @include flex-direction(column);
    flex: 1;
    width: 100%;
    max-width: rem(720px);
    min-height: 0;
    gap: rem(12px);
    overflow-y: auto;
}

.andy-agents__thinking {
    @include display-flex();
    @include align-items(center);
    @include justify-content(center);
    align-self: center;
    flex-shrink: 0;
    gap: rem(8px);
}

.andy-agents__thinking-mascot ::v-deep .am-think > circle {
    fill: $color-warning-500;
}

.andy-agents__thinking-text {
    @include font-size(s);
    margin: 0;
    font-family: $text;
    color: $color-black;
}

.andy-agents__message {
    @include display-flex();
    @include flex-direction(column);
    max-width: 80%;
    gap: rem(12px);
    font-family: $text;
    color: $color-black;

    &.is-user {
        align-self: flex-end;
    }

    &.is-assistant {
        align-self: flex-start;
    }

    &.has-widgets {
        width: 100%;
        max-width: 100%;
    }
}

.andy-agents__copy {
    @include border-radius(rem(8px));
    @include font-size(m);
    padding: rem(12px) rem(16px);

    p {
        margin: 0;

        + p {
            margin-top: rem(12px);
        }
    }

    .is-user & {
        background-color: $color-secondary-100;
        white-space: pre-wrap;
    }

    .is-assistant & {
        padding: 0;
        background-color: transparent;
        border: 0;
    }

    .has-widgets & {
        max-width: 80%;
    }
}

.andy-agents__dock {
    flex-shrink: 0;
    width: 100%;
    max-width: rem(720px);
}

.andy-agents__composer {
    @include border-radius(rem(8px));
    position: relative;
    width: 100%;
    background-color: $color-white;
    border: rem(1px) solid $color-neutral-300;
}

.andy-agents__input {
    @include font-size(m);
    display: block;
    width: 100%;
    min-height: 60px;
    margin: 0;
    padding: rem(16px) rem(56px) rem(52px) rem(16px);
    border: 0;
    background: transparent;
    resize: none;
    font-family: $text;
    color: $color-black;

    &:focus {
        outline: none;
    }
}

.andy-agents.is-chat .andy-agents__input {
    box-sizing: border-box;
    height: rem(52px);
    min-height: rem(52px);
    max-height: rem(227px);
    padding: rem(16px) rem(56px) rem(16px) rem(16px);
    line-height: rem(20px);
    overflow-x: hidden;
    overflow-y: auto;
}

.andy-agents__send {
    @include display-flex();
    @include align-items(center);
    @include justify-content(center);
    @include border-radius(rem(4px));
    position: absolute;
    right: rem(12px);
    bottom: rem(12px);
    width: rem(36px);
    height: rem(36px);
    margin: 0;
    padding: 0;
    border: rem(1px) solid $color-neutral-300;
    background-color: $color-white;
    color: $color-secondary-500;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}
</style>
